import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
} from "@angular/common/http";
import { NgModule } from "@angular/core";
import {
  FormControl,
  FormGroupDirective,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
} from "@angular/forms";
import { APP_BASE_HREF, DecimalPipe } from "@angular/common";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ScrollingModule } from "@angular/cdk/scrolling";
import {
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
} from "@angular/material/core";
import { provideFirebaseApp, initializeApp } from "@angular/fire/app";
import {
  provideRemoteConfig,
  getRemoteConfig,
} from "@angular/fire/remote-config";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { NgScrollbarModule } from "ngx-scrollbar";
import { NgSelectModule } from "@ng-select/ng-select";
import {
  BreadcrumbRouterComponent,
  BreadcrumbRouterService,
} from "@coreui/angular";
import { AppRoutingModule } from "../routes/app-routing.module";
import { AngularMaterialModule } from "src/modules/angular-material.module";
import { CoreUIAngularModule } from "src/modules/coreui-angular.module";
import { UserModule } from "src/pages/users/user.module";
import { SharedAppModule } from "src/pages/shared/shared.module";

import { InterceptorService } from "src/services/interceptor.service";
import { EncryptionInterceptor } from "src/encryption/encryption.interceptor";

import { AppComponent } from "./app.component";

import { firebaseConfig } from "src/constants/firebase-config";
import { idbConfig } from "src/constants/idb-config";
import { environment } from "src/environments/environment";
import { NgxDaterangepickerMd } from "ngx-daterangepicker-material";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { SafeHTMLPipe } from "src/pipes/safe-html.pipe";
import { attachEncodedDataKeyInterceptor } from "src/services/attachEncodedDataKeyInterceptor.service";

function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, "./assets/i18n/", ".json");
}

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "DD/MM/YYYY",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control && control.invalid && control.touched);
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    CoreUIAngularModule,
    NgScrollbarModule,
    SharedAppModule,
    UserModule,
    NgSelectModule,
    ScrollingModule,
    NgxDaterangepickerMd.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideRemoteConfig(() => getRemoteConfig()),
    NgbModule,
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: environment.base_href },
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncryptionInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptors([attachEncodedDataKeyInterceptor])),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: "en-GB" },
    { provide: ErrorStateMatcher, useValue: new MyErrorStateMatcher() },
    { provide: BreadcrumbRouterService, useClass: BreadcrumbRouterComponent },
    {
      provide: "idbInstance",
      useFactory: () => {
        // Check if IndexedDB is supported or not.
        if (!window.indexedDB) {
          return;
        }

        const { dbStoreName, objectStoreName } = idbConfig;

        return new Promise((resolve, reject) => {
          // Initializing Indexed DB.
          const openDBReq = window.indexedDB.open(dbStoreName);

          openDBReq.onsuccess = () => {
            const db = openDBReq.result;

            if (db.objectStoreNames.contains(objectStoreName)) {
              resolve(db);
            } else {
              // Closing the previous opened DB.
              db.close();

              // If object store is not present then update the current IDB version.
              const newDBReq = window.indexedDB.open(
                dbStoreName,
                db.version + 1
              );

              newDBReq.onupgradeneeded = (event: any) => {
                const transaction: IDBTransaction = event.target!.transaction;
                const newDB = newDBReq.result;

                newDB.createObjectStore(objectStoreName, {
                  keyPath: "id",
                });

                transaction.oncomplete = () => {
                  resolve(newDB);
                };
              };

              newDBReq.onerror = () => {
                console.log("Error occurred in IndexedDB", newDBReq.error);
                resolve(undefined);
              };
            }
          };

          openDBReq.onerror = () => {
            console.log("Error occurred in IndexedDB", openDBReq.error);
            resolve(undefined);
          };
        });
      },
      deps: [],
    },
    DecimalPipe,
    SafeHTMLPipe,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
